/**
 * Client-side capture configuration — ARCHITECTURE.md §4, §8, §10, §11.
 *
 * Quality thresholds here are PROVISIONAL client-side gates (the authoritative gates are
 * recomputed server-side, §22). They are tuned for the fixed analysis size below so the
 * numbers stay comparable across devices; production values move to signed remote
 * config in Phase 8 (§24).
 */

/** Analysis frames are downscaled to this width before metrics run (§8 normalization). */
export const ANALYSIS_WIDTH = 360;

/** §8 — quality analysis cadence; recording itself stays at 30 FPS. */
export const ANALYSIS_FPS = 10;

/** §4 — card overlay geometry relative to the viewport. */
export const CARD_OVERLAY = {
  widthRatio: 0.84,
  /** ISO ID-1 card aspect (85.6 × 53.98 mm). */
  cardAspect: 85.6 / 53.98,
  /** §6 — focus/sharpness target is the inner 60% of the card. */
  innerRegionRatio: 0.6,
  /** Border band (relative to card size) used for edge-presence detection. */
  borderBandRatio: 0.08,
} as const;

/** §18 — face oval geometry relative to the viewport. */
export const FACE_OVAL = {
  widthRatio: 0.62,
  heightRatio: 0.42,
  centerYRatio: 0.42,
} as const;

/**
 * Client-side quality thresholds at ANALYSIS_WIDTH. Calibrated for a HAND-HELD phone
 * (no tripod): the bar is "the card/face is in focus and readable", not "zero movement".
 * Motion tolerance is deliberately generous — genuine motion blur is caught by the
 * sharpness gate, so natural hand shake should never block capture. The authoritative
 * checks still run server-side (§22); these keep the on-device experience smooth. Final
 * per-region values come from signed remote config after dataset calibration (§8/§24).
 */
export const CLIENT_THRESHOLDS = {
  /** Laplacian variance on the card inner ROI (blur detector). */
  cardSharpnessMin: 18,
  /** Laplacian variance on the face ROI — skin is smooth, so this stays low; the
   * backend recomputes authoritative sharpness on the recording (§22). */
  faceSharpnessMin: 6,
  /** Mean absolute frame difference (0–255) above which motion fails — generous for handheld. */
  motionMax: 34,
  /** Fraction of near-saturated pixels allowed on the card ROI. */
  glareRatioMax: 0.07,
  /** Largest saturated blob (fraction of ROI) allowed — a compact blob is glare. */
  glareBlockMax: 0.045,
  underExposureMax: 0.6,
  overExposureMax: 0.14,
  contrastMin: 10,
  /** Border-band edge density needed to call a card side "present". */
  cardEdgeDensityMin: 0.03,
  /** Sides (of 4) whose border band must show card edges. */
  cardEdgeSidesMin: 2,
  /** Inner-ROI contrast floor for card presence. */
  cardInnerContrastMin: 8,
} as const;

/** §10 — user guidance strings. */
export const MESSAGES = {
  cardMissing: 'Place your card inside the frame',
  cardSmall: 'Move the card closer',
  tooClose: 'Move the card back a little',
  misaligned: 'Fit all four corners inside the frame',
  focusing: 'Focusing…',
  blur: 'Hold your phone and card still',
  glare: 'Remove the reflection',
  lowLight: 'Increase the lighting',
  ready: 'Hold still',
  recording: 'Keep the card in position',
  tilted: 'Keep the phone and card straight',
  faceMissing: 'Keep your face inside the oval',
  faceMultiple: 'Only you should be in the frame',
  faceLookStraight: 'Look straight at the camera',
  faceReturnCenter: 'Look back at the centre',
  moveLeft: 'Slowly turn LEFT',
  moveRight: 'Slowly turn RIGHT',
  moveUp: 'Slowly look UP',
  moveDown: 'Slowly look DOWN',
  flipCard: 'Now flip the card and show the back side',
  cardTooClose: 'Move the card back a little',
  adjustGlare: 'Tilt the card slightly to remove the reflection',
  stillPlease: 'Hold the phone and card completely still',
} as const;

/** Where the self-hosted MediaPipe assets live (see scripts/fetch-models.mjs). */
export const MEDIAPIPE_WASM_PATH = '/mediapipe-wasm';
export const FACE_MODEL_PATH = '/models/face_landmarker.task';

export const API_BASE = '/api/v1';
